/**
 * Form alanı serbest 2D ızgara yerleşimi — paylaşılan yardımcılar.
 * Her alan (colStart 0-11, rowStart >=0, colSpan 1-12). Builder'da hücreler tek satır yüksekliğinde;
 * fill'de değişken yükseklik. Konumsuz (null) alanlar sortOrder ile otomatik paketlenir.
 */
export const GRID_COLS = 12;
export const BUILDER_ROW_H = 46; // builder kanvas satır yüksekliği (px)
export const BUILDER_ROW_GAP = 8;
export const MAX_ROW = 400; // sunucu sınırı (500) ALTINDA tut → kaydedilemez form oluşmasın

type PosField = { colStart: number; rowStart: number; colSpan: number; rowSpan?: number | null; type?: string };

/** Konumsuz alanları (verilen sırayla) 12-kolon ızgaraya soldan-sağa paketle (mevcut auto-flow görünümünü korur). */
export function autoPackPositions<T extends { colSpan: number }>(fields: T[]): Array<T & { colStart: number; rowStart: number }> {
  let row = 0, col = 0;
  return fields.map((f) => {
    const span = Math.min(GRID_COLS, Math.max(1, f.colSpan));
    if (col + span > GRID_COLS) { row++; col = 0; }
    const placed = { ...f, colStart: col, rowStart: row };
    col += span;
    if (col >= GRID_COLS) { row++; col = 0; }
    return placed;
  });
}

function occupancy(fields: PosField[], excludeIdx: number): Set<string> {
  const occ = new Set<string>();
  fields.forEach((f, i) => {
    if (i === excludeIdx || f.type === "card") return; // kart = arka plan → alan yerleşimini engellemez
    const rs = f.rowSpan && f.rowSpan > 1 ? f.rowSpan : 1;
    for (let r = f.rowStart; r < f.rowStart + rs; r++) for (let c = f.colStart; c < f.colStart + f.colSpan; c++) occ.add(`${r}:${c}`);
  });
  return occ;
}
/** (row..row+rowSpan) × (col..col+span) bölgesi boş mu — taşınan elemanın YÜKSEKLİĞİ (rowSpan) dahil. */
function isFree(occ: Set<string>, row: number, col: number, span: number, rowSpan = 1): boolean {
  for (let r = row; r < row + rowSpan; r++) for (let c = col; c < col + span; c++) if (occ.has(`${r}:${c}`)) return false;
  return true;
}
const rs = (v: number | null | undefined) => (v && v > 1 ? v : 1);

/** Hedef (wantCol,wantRow)'a en yakın ÇAKIŞMASIZ yerleşimi bul (movingIdx = taşınan alanın index'i, çakışma dışı). rowSpan = taşınan yükseklik. */
export function resolvePlacement(fields: PosField[], movingIdx: number, wantCol: number, wantRow: number, span: number, rowSpan = 1): { colStart: number; rowStart: number } {
  const occ = occupancy(fields, movingIdx);
  const col = Math.min(GRID_COLS - span, Math.max(0, wantCol));
  const row = Math.min(MAX_ROW, Math.max(0, wantRow));
  if (isFree(occ, row, col, span, rowSpan)) return { colStart: col, rowStart: row };
  // Aynı satırda hedefe en yakın boş başlangıcı ara.
  for (let d = 1; d <= GRID_COLS; d++) {
    const r = col + d; if (r <= GRID_COLS - span && isFree(occ, row, r, span, rowSpan)) return { colStart: r, rowStart: row };
    const l = col - d; if (l >= 0 && isFree(occ, row, l, span, rowSpan)) return { colStart: l, rowStart: row };
  }
  // Satır dolu → aşağı doğru ilk boş satır (hedef kolonu koru); alt kenar MAX_ROW'u aşmasın.
  for (let rr = row + 1; rr + rowSpan - 1 <= MAX_ROW; rr++) if (isFree(occ, rr, col, span, rowSpan)) return { colStart: col, rowStart: rr };
  return { colStart: col, rowStart: row }; // ızgara dolu (gerçekçi değil) → hedefte kal
}

/** Dikey taşıma: KOLONU koruyarak yönündeki ilk boş satır (yatay "ışınlanma" olmaz). Yer yoksa aynı satır. */
export function nextFreeRow(fields: PosField[], movingIdx: number, col: number, fromRow: number, dir: -1 | 1, span: number, rowSpan = 1): number {
  const occ = occupancy(fields, movingIdx);
  for (let r = fromRow + dir; r >= 0 && r + rowSpan - 1 <= MAX_ROW; r += dir) if (isFree(occ, r, col, span, rowSpan)) return r;
  return fromRow;
}

/** Yatay taşıma: SATIRI koruyarak yönündeki ilk boş kolon. Yer yoksa aynı kolon. */
export function nextFreeCol(fields: PosField[], movingIdx: number, row: number, fromCol: number, dir: -1 | 1, span: number, rowSpan = 1): number {
  const occ = occupancy(fields, movingIdx);
  for (let c = fromCol + dir; c >= 0 && c <= GRID_COLS - span; c += dir) if (isFree(occ, row, c, span, rowSpan)) return c;
  return fromCol;
}

/**
 * Genişliği MUTLAK değere ayarla (slider + ± butonları tek yol). colStart ızgara dışına taşmayacak şekilde kısılır;
 * yeni genişlik yerinde ÇAKIŞIYORSA değişiklik yapılmaz (komşunun üstüne binmesin) — küçültme daima güvenli.
 */
export function applySpan<T extends PosField>(fields: T[], idx: number, nextSpan: number): T[] {
  const f = fields[idx];
  if (!f) return fields;
  const span = Math.min(GRID_COLS, Math.max(1, nextSpan));
  if (span === f.colSpan) return fields;
  const colStart = Math.min(f.colStart, GRID_COLS - span); // sağa taşarsa sola kaydır
  const occ = occupancy(fields, idx);
  if (!isFree(occ, f.rowStart, colStart, span, rs(f.rowSpan))) return fields; // yer yok → büyütme (çakışma engeli)
  return fields.map((x, i) => (i === idx ? { ...x, colSpan: span, colStart } : x));
}

/** Boyutlandırma: (w,h) hedef bölgesi (mevcut konumdan, kendisi hariç) BOŞ ise kabul; değilse null (çakışma engeli).
 *  İmleç dışa çekilirken çakışan ilk adım reddedilir → kart komşuya değene kadar büyür, orada durur. */
export function resolveResize(fields: PosField[], idx: number, wantW: number, wantH: number, maxH = 50): { w: number; h: number } | null {
  const f = fields[idx];
  if (!f) return null;
  const w = Math.min(GRID_COLS - f.colStart, Math.max(1, wantW));
  const h = Math.min(maxH, Math.max(1, wantH));
  const occ = occupancy(fields, idx);
  return isFree(occ, f.rowStart, f.colStart, w, h) ? { w, h } : null;
}

/** Yeni alan için yerleşim: son satırda yer varsa oraya, yoksa alta yeni satır. */
export function appendSlot(fields: PosField[], span: number): { colStart: number; rowStart: number } {
  if (fields.length === 0) return { colStart: 0, rowStart: 0 };
  const mr = maxRow(fields);
  const occ = occupancy(fields, -1);
  for (let c = 0; c <= GRID_COLS - span; c++) if (isFree(occ, mr, c, span)) return { colStart: c, rowStart: mr };
  return { colStart: 0, rowStart: mr + 1 };
}

/** En alttaki dolu satır (kart/dikey-çizgi rowSpan'i dahil). */
export function maxRow(fields: Array<{ rowStart: number; rowSpan?: number | null }>): number {
  return fields.reduce((m, f) => Math.max(m, f.rowStart + ((f.rowSpan && f.rowSpan > 1 ? f.rowSpan : 1) - 1)), 0);
}
